import React, { useEffect, useState } from "react";
import { CheckCircle, XCircle, Loader } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { verifyTrial } from "../api/apiFunction/userServices";

const TrialVerify = () => {
  const [status, setStatus] = useState("loading");
  const [message, setMessage] = useState("");
  const [trialDetails, setTrialDetails] = useState(null);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  useEffect(() => {
    const verifyTrialActivation = async () => {
      try {
        // Get stripe_id from URL (Stripe Checkout Session ID)
        const stripeId = searchParams.get("stripe_id");

        if (!stripeId) {
          setStatus("error");
          setMessage("Missing payment session ID. Please try again.");
          return;
        }

        // Call verify trial API
        const res = await verifyTrial(stripeId);

        if (res?.data?.success) {
          setStatus("success");
          setMessage(res.data.message || "Trial activated successfully!");

          // Set trial details for display
          setTrialDetails({
            daysRemaining: res.data.user?.trial?.daysRemaining || 7,
            planName: res.data.user?.plan?.name || "Pro",
            trialEnd: res.data.subscription?.trialEnd,
          });

          // Update localStorage: merge latest user (including trial + plan)
          const existingUser = localStorage.getItem("user")
            ? JSON.parse(localStorage.getItem("user"))
            : null;

          if (res.data.user) {
            // Preserve any existing fields like token, but take fresh trial/plan/etc. from backend
            const mergedUser = existingUser
              ? { ...existingUser, ...res.data.user }
              : res.data.user;
            localStorage.setItem("user", JSON.stringify(mergedUser));
          }

          // Clean up stored session ID
          localStorage.removeItem("trial_session_id");
        } else {
          setStatus("error");
          setMessage(
            res?.data?.message || res?.data?.error || "Trial activation failed"
          );
        }
      } catch (error) {
        setStatus("error");

        const errorMsg =
          error?.response?.data?.error ||
          error?.response?.data?.message ||
          "Failed to verify trial. Please contact support.";
        setMessage(errorMsg);

        toast.error(errorMsg);
      }
    };

    verifyTrialActivation();
  }, [searchParams, navigate]);

  const handleGoToChat = () => {
    navigate("/chat", { replace: true });
  };

  const handleRetry = () => {
    navigate("/activate-trial", { replace: true });
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-bg-50 px-4">
      <div className="max-w-md w-full text-center">
        {/* Status Icon */}
        <div className="mb-6">
          <div
            className={`mx-auto w-20 h-20 rounded-full flex items-center justify-center ${
              status === "loading"
                ? "bg-blue-100 dark:bg-blue-900"
                : status === "success"
                ? "bg-green-100 dark:bg-green-900"
                : "bg-red-100 dark:bg-red-900"
            }`}
          >
            {status === "loading" && (
              <Loader className="w-10 h-10 text-blue-600 animate-spin" />
            )}
            {status === "success" && (
              <CheckCircle className="w-10 h-10 text-green-600" />
            )}
            {status === "error" && (
              <XCircle className="w-10 h-10 text-red-600" />
            )}
          </div>
        </div>

        {/* Title */}
        <h1 className="text-3xl font-bold mb-4 text-fg-50">
          {status === "loading" && "Activating Your Trial..."}
          {status === "success" && "Trial Activated! 🎉"}
          {status === "error" && "Activation Failed"}
        </h1>

        {/* Message */}
        <p className="text-fg-60 text-lg mb-8">
          {status === "loading" &&
            "Please wait while we set up your free trial..."}
          {status === "success" &&
            "Your 7-day free trial is now active. Enjoy all Pro features!"}
          {status === "error" &&
            (message || "Something went wrong. Please try again.")}
        </p>

        {/* Trial Details (Success Only) */}
        {status === "success" && trialDetails && (
          <div className="bg-bg-60 rounded-xl p-6 mb-8 text-left">
            <div className="flex items-center gap-2 mb-4">
              <h3 className="font-semibold text-fg-50">Trial Details</h3>
            </div>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-fg-60">Plan</span>
                <span className="text-fg-50 font-medium">
                  {trialDetails.planName.charAt(0).toUpperCase() +
                    trialDetails.planName.slice(1)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-fg-60">Days Remaining</span>
                <span className="text-fg-50 font-medium">
                  {trialDetails.daysRemaining} days
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-fg-60">Status</span>
                <span className="text-fg-50  font-medium">Active</span>
              </div>
              {trialDetails.trialEnd && (
                <div className="flex justify-between">
                  <span className="text-fg-60">Trial Ends</span>
                  <span className="text-fg-50 font-medium">
                    {new Date(trialDetails.trialEnd).toLocaleDateString()}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        {status === "success" && (
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => navigate("/")}
              className="bg-transparent border border-bd-50 text-fg-50 py-3 px-4 rounded-full font-medium hover:bg-bg-60 transition-all duration-200"
            >
              Back to Home
            </button>
            <button
              onClick={handleGoToChat}
              className="bg-ac-02 text-white dark:text-black py-3 px-4 rounded-full font-medium hover:bg-ac-01 transition-all duration-200"
            >
              Go to Chat
            </button>
          </div>
        )}

        {status === "error" && (
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => navigate("/")}
              className="bg-transparent border border-bd-50 text-fg-50 py-3 px-4 rounded-full font-medium hover:bg-bg-60 transition-all duration-200"
            >
              Back to Home
            </button>
            <button
              onClick={handleRetry}
              className="bg-ac-02 text-white dark:text-black py-3 px-4 rounded-full font-medium hover:bg-ac-01 transition-all duration-200"
            >
              Try Again
            </button>
          </div>
        )}

        {/* Help Text */}
        <div className="mt-8 text-sm text-fg-60">
          <p>
            Need help? Contact our{" "}
            <a
              href="/contact"
              className="text-ac-02 hover:text-ac-01 underline"
            >
              support team
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default TrialVerify;
